import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiPlus, FiDownload, FiCheck, FiChevronDown, FiClock, FiMail, FiShield, FiInfo } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { toast } from '../Toast';
import { displayName } from '../Header';
import { IGlobalState } from '../../../model/IGlobalState';
import { HolderDetailsResult } from '../../../model/HolderDetailsResult';
import Stock from '../../../model/Stock';
import { useContactsData, listName } from '../communications/contacts/contactsData';
import { CONSENT_SOURCES } from '../communications/contacts/AddContactModal';
import { ReportModal } from './ReportModals';
import { saveCsv } from './reportsData';
import { fmtDate, movementDescription, activityTitle } from './HolderDetail';

// Holder detail pop-ups: Add to contacts (Figma 532:1724, note 537:2132) and Export holder report (532:2374, note 537:2147),
// on the ReportModal shell. Nothing here writes: Add to Contacts is preview only; the export is a CSV built in the browser
// from the holder already loaded, or the printable page (HolderReportView.tsx) which reads the same GET again.

interface Props { stock: Stock; result: HolderDetailsResult; onClose: () => void }

// "MR KENNETH JAMES PARKER <PARKER FAMILY A/C>" → Kenneth / Parker; companies and trusts keep their whole name as the first name
const TITLES = /^(mr|mrs|ms|miss|mstr|master|dr|prof|sir|dame|rev)\.?$/i;
const titleCase = (s: string) => s.toLowerCase().replace(/(^|[\s'-])([a-z])/g, (_, p, c) => p + c.toUpperCase());
const splitHolderName = (name: string, holderType: string) => {
    const bare = name.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    const words = bare.split(' ');
    const person = /individual|joint/i.test(holderType || '') || TITLES.test(words[0] || '');
    if (!person) return { first: titleCase(bare), last: '' };
    const parts = words.filter(w => !TITLES.test(w));
    return { first: titleCase(parts[0] ?? ''), last: parts.length > 1 ? titleCase(parts[parts.length - 1]) : '' };
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// what the contact would receive; contacts carry no such preference yet, so these are part of the preview
const RECEIVE = [
    { key: 'announcements', title: 'ASX announcements', sub: 'Sent automatically when an announcement is released' },
    { key: 'news', title: 'Company news & updates', sub: 'Campaigns you write yourself' },
    { key: 'events', title: 'Event invitations', sub: 'Webinars, AGM, site visits' },
];

const Tick: React.FC<{ on: boolean }> = ({ on }) => <span className={`holder-modal-box${on ? ' on' : ''}`}>{on && <FiCheck />}</span>;

export const AddHolderToContactsModal: React.FC<Props> = ({ stock, result, onClose }) => {
    const user = useSelector((state: IGlobalState) => state.user);
    const holder = result.holder!;
    const company = stock.companyName || stock.ticker;
    // reads only: the contact lists and their members (GET /api/ContactList/Lists + /api/ContactList), to list them and spot a duplicate
    const { data, error } = useContactsData(stock.id);
    const initial = splitHolderName(holder.name, holder.holderType);
    const [first, setFirst] = useState(initial.first);
    const [last, setLast] = useState(initial.last);
    const [email, setEmail] = useState(holder.email || '');
    const [list, setList] = useState('');
    const [receive, setReceive] = useState<string[]>(['announcements', 'news']);
    const [agreed, setAgreed] = useState(false);
    const [how, setHow] = useState(CONSENT_SOURCES[0]);

    const existing = data?.contacts.find(c => c.email.toLowerCase() === email.trim().toLowerCase());
    const onLists = (data?.lists || []).filter(l => existing?.listIds.includes(l.id));

    // default to a list they aren't on yet: the announcement list first
    useEffect(() => {
        if (!data || list) return;
        const open = data.lists.filter(l => !existing?.listIds.includes(l.id));
        setList((open.find(l => l.id === data.defaultListId) ?? open[0] ?? data.lists[0])?.id ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const valid = EMAIL.test(email.trim()) && agreed && !!list;
    const fullName = [first, last].filter(Boolean).join(' ') || holder.name;
    const save = () => {
        toast.info(`Preview only: ${fullName} wasn’t added to Contacts — nothing was saved.`);
        onClose();
    };
    const toggle = (key: string) => setReceive(receive.includes(key) ? receive.filter(k => k !== key) : [...receive, key]);

    return <ReportModal className="contacts-modal wide holder-modal contacts" icon={<FiPlus />} title="Add This Holder to Contacts"
        sub="So they receive announcement emails and campaigns" note="Australian spam rules: you must be able to show how and when each person agreed." onClose={onClose}
        buttons={<button type="button" className="btn-primary" disabled={!valid} onClick={save}><FiPlus /> Add to Contacts</button>}>
        <div className="contacts-add">
            <div className="col">
                <div className="pair">
                    <label className="contacts-field"><span>First name</span><input value={first} onChange={e => setFirst(e.target.value)} /></label>
                    <label className="contacts-field"><span>Last name</span><input value={last} onChange={e => setLast(e.target.value)} /></label>
                </div>
                <label className="contacts-field"><span>Email <em>{holder.email ? '· from the registry' : '· the registry has no email for this holder'}</em></span>
                    <span className="with-icon"><FiMail /><input type="email" value={email} placeholder="name@example.com" onChange={e => setEmail(e.target.value)} /></span>
                </label>
                <label className="contacts-field"><span>Add to which list</span>
                    <span className="contacts-select">
                        <select aria-label="Add to which list" value={list} disabled={!data?.lists.length} onChange={e => setList(e.target.value)}>
                            {!data && <option value="">{error ? 'Lists couldn’t be loaded' : 'Loading lists…'}</option>}
                            {data && !data.lists.length && <option value="">No lists yet</option>}
                            {(data?.lists || []).map(l => <option key={l.id} value={l.id}>{listName(l)} ({l.numSubscribers.toLocaleString()} subscribed){existing?.listIds.includes(l.id) ? ' — already on it' : ''}</option>)}
                        </select>
                        <FiChevronDown />
                    </span>
                </label>
                <span className="holder-modal-label">What they’ll receive</span>
                {RECEIVE.map(r => <button type="button" key={r.key} role="checkbox" aria-checked={receive.includes(r.key)}
                    className={`holder-modal-option${receive.includes(r.key) ? ' on' : ''}`} onClick={() => toggle(r.key)}>
                    <Tick on={receive.includes(r.key)} />
                    <span className="t"><strong>{r.title}</strong><span>{r.sub}</span></span>
                </button>)}
            </div>
            <div className="col">
                <div className="contacts-permission">
                    <div className="h"><ShieldCheckIcon /> <strong>Permission to email this person</strong> <span className="req">Required</span></div>
                    <label className={`confirm${agreed ? ' on' : ''}`}>
                        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                        <span><strong>I confirm {fullName} has agreed to receive investor emails from {company}.</strong>
                            <small>You can’t add someone who has not agreed. Adding people without permission breaches the Spam Act 2003 (Cth).</small></span>
                    </label>
                    <span className="lbl">How did they agree?</span>
                    <span className="contacts-select">
                        <select aria-label="How did they agree?" value={how} onChange={e => setHow(e.target.value)}>{CONSENT_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}</select>
                        <FiChevronDown />
                    </span>
                    <p className="rec"><FiClock /> Recorded against this contact as: {displayName(user)} · {moment().format('D MMMM YYYY')} · added from the share register</p>
                </div>
                {existing && <p className="holder-modal-note"><FiCheck /> {email.trim()} is already a contact{onLists.length ? ` on ${onLists.map(listName).join(', ')}` : ''}{existing.subscribed ? '' : ' (unsubscribed)'}, so Investi would add them to the chosen list rather than create a duplicate.</p>}
                {!existing && result.subscribedOn && <p className="holder-modal-note"><FiCheck /> This holder subscribed on {fmtDate(result.subscribedOn)}.</p>}
                {error && <p className="holder-modal-note muted"><FiInfo /> Your contact lists couldn’t be loaded, so we can’t check whether they’re already a contact.</p>}
            </div>
        </div>
    </ReportModal>;
};

// ---- Export holder report ----

type Section = 'details' | 'chart' | 'movements' | 'activity';
const SECTIONS: { key: Section; label: string }[] = [
    { key: 'details', label: 'Holder details and current position' },
    { key: 'chart', label: 'Holding and share price chart' },
    { key: 'movements', label: 'Full movement history' },
    { key: 'activity', label: 'Website and email activity' },
];
export const PERIODS: Record<string, { label: string; days: number }> = {
    all: { label: 'Since they first invested', days: Infinity },
    '365': { label: 'Last 12 months', days: 365 },
    '90': { label: 'Last 90 days', days: 90 },
    '30': { label: 'Last 30 days', days: 30 },
};
export const periodStart = (period: string) => {
    const days = (PERIODS[period] ?? PERIODS.all).days;
    return days === Infinity ? '' : moment().subtract(days, 'days').format('YYYY-MM-DD');
};
export const holderReportUrl = (stock: Stock, holderId: number, sections: string[], period: string) =>
    `/registry-report/${stock.ticker}:${stock.exchange}/holder/${holderId}?include=${sections.join(',')}&period=${period}`;

// one CSV with a block per section; the chart has no CSV form, so it's PDF only
const holderCsv = (stock: Stock, result: HolderDetailsResult, sections: Section[], period: string) => {
    const h = result.holder!;
    const s = result.summary;
    const from = periodStart(period);
    const lines: (string | number)[][] = [[`${stock.companyName || stock.ticker} — holder report`, PERIODS[period].label], []];
    if (sections.includes('details')) {
        lines.push(['Holder details'], ['Name', h.name], ['Holder number', h.holderNumber], ['Holder ID', h.holderId], ['Type', h.holderType || ''],
            ['Address', h.address || ''], ['Email', h.email || ''], ['Phone', h.phone || ''], ['First invested', fmtDate(h.firstInvestmentDate)]);
        if (s) lines.push(['Shares held', s.currentBalance], ['Current price', s.currentPrice], ['Current value', s.currentValue.toFixed(2)],
            ['% of issued capital', result.issuedCapital ? (s.currentBalance * 100 / result.issuedCapital).toFixed(4) : '']);
        lines.push([]);
    }
    if (sections.includes('movements')) {
        lines.push(['Movement history'], ['Date', 'Security', 'Description', 'Broker', 'Movement', 'Price', 'Price estimated', 'Closing balance'],
            ...[...(result.movements || [])].reverse().filter(m => m.date.slice(0, 10) >= from)
                .map(m => [m.date.slice(0, 10), m.securityCode || stock.ticker, movementDescription(m), m.broker || '', m.movement, m.price ?? '', m.priceEstimated ? 'yes' : '', m.closingBalance]), []);
    }
    if (sections.includes('activity')) {
        lines.push(['Website and email activity'], ['Date', 'Type', 'What', 'Detail'],
            ...(result.activity || []).filter(a => a.date.slice(0, 10) >= from)
                .map(a => [moment(a.date).format('YYYY-MM-DD HH:mm'), a.type, activityTitle(a), [a.meta, a.actions?.join(' · ')].filter(Boolean).join(' · ')]));
    }
    return lines;
};

export const ExportHolderReportModal: React.FC<Props> = ({ stock, result, onClose }) => {
    const [format, setFormat] = useState<'pdf' | 'csv'>('pdf');
    const [period, setPeriod] = useState('all');
    const [sections, setSections] = useState<Section[]>(['details', 'chart', 'movements']);
    const holder = result.holder!;
    const toggle = (key: Section) => setSections(sections.includes(key) ? sections.filter(k => k !== key) : [...sections, key]);
    const picked = SECTIONS.filter(x => sections.includes(x.key)).map(x => x.key);
    const nothing = !picked.length || (format === 'csv' && picked.every(k => k === 'chart'));

    const download = () => {
        if (format === 'pdf') {
            window.open(holderReportUrl(stock, holder.holderId, picked, period), '_blank', 'noopener');
            onClose();
            return;
        }
        saveCsv(`${stock.ticker}-holder-${holder.holderNumber || holder.holderId}.csv`, holderCsv(stock, result, picked, period));
        onClose();
    };

    return <ReportModal className="holder-modal export" icon={<FiDownload />} title="Export This Holder’s Report"
        sub="A PDF or a spreadsheet of everything on this page" note="" onClose={onClose}
        buttons={<button type="button" className="btn-primary" disabled={nothing} onClick={download}><FiDownload /> Download</button>}>
        <div className="holder-modal-pair">
            <label className="report-modal-field">
                <span>Format</span>
                <span className="report-modal-select">
                    <select value={format} onChange={e => setFormat(e.target.value as 'pdf' | 'csv')}>
                        <option value="pdf">PDF (print or save)</option>
                        <option value="csv">Spreadsheet (CSV)</option>
                    </select>
                    <FiChevronDown />
                </span>
            </label>
            <label className="report-modal-field">
                <span>Period</span>
                <span className="report-modal-select">
                    <select value={period} onChange={e => setPeriod(e.target.value)}>{Object.keys(PERIODS).map(k => <option key={k} value={k}>{PERIODS[k].label}</option>)}</select>
                    <FiChevronDown />
                </span>
            </label>
        </div>
        <span className="holder-modal-label">Include</span>
        {SECTIONS.map(x => <button type="button" key={x.key} role="checkbox" aria-checked={sections.includes(x.key)} className="holder-modal-tick" onClick={() => toggle(x.key)}>
            <Tick on={sections.includes(x.key)} /> {x.label}{x.key === 'chart' && format === 'csv' && <em>PDF only</em>}
        </button>)}
        <button type="button" className="holder-modal-tick" disabled><Tick on={false} /> Notes your team has added <em>Not connected yet</em></button>
        <p className="holder-modal-note warn"><FiShield /> Registry data is confidential — keep the file secure and share it only with people entitled to see it.</p>
    </ReportModal>;
};
